/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { IAutocompleteOption } from "src/shared/types/field.types";
import AutocompleteFieldInner, {
  IAutocompleteFieldInnerProps,
} from "./AutocompleteFieldInner";

export interface IAutocompleteFieldProps<T = string, Multiple = true>
  extends Omit<IAutocompleteFieldInnerProps<T>, "onChange" | "value"> {
  onChange?: (
    v: Multiple extends true ? T[] : T | null,
    e: SyntheticEvent<Element, Event>,
  ) => void;
  value?: Multiple extends true ? T[] : T | null;
  chipAvatar?: (value: IAutocompleteOption<T>) => JSX.Element;
}

function AutocompleteField<T, Multiple>(
  { onChange, value, ...selectProps }: IAutocompleteFieldProps<T, Multiple>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const [fieldValue, setFieldValue] = useState<
    NonNullable<IAutocompleteOption<T>> | IAutocompleteOption<T>[] | null
  >(selectProps.multiple ? [] : null);

  const onFieldChange = (
    e: SyntheticEvent<Element, Event>,
    v:
      | NonNullable<T | IAutocompleteOption<T>>
      | (T | IAutocompleteOption<T>)[]
      | null,
  ): void => {
    if (!onChange) return;

    // multiple
    if (selectProps.multiple) {
      const arr = Array.isArray(v) ? (v as (T | IAutocompleteOption<T>)[]) : [];
      const result = arr.map((x) =>
        x && typeof x === "object" && "value" in (x as any)
          ? (x as IAutocompleteOption<T>).value
          : (x as T),
      ) as Multiple extends true ? T[] : T;
      onChange(result, e);

      return;
    }

    // single
    let resultSingle: T | null = null;
    if (v == null) {
      resultSingle = null;
    } else if (typeof v === "object" && "value" in (v as any)) {
      resultSingle = (v as IAutocompleteOption<T>).value as T;
    } else {
      resultSingle = v as T;
    }
    onChange(resultSingle as Multiple extends true ? T[] : T, e);
  };

  const computedFieldValue:
    | NonNullable<IAutocompleteOption<T>>
    | IAutocompleteOption<T>[]
    | null = useMemo(() => {
    if (selectProps.multiple && Array.isArray(value)) {
      return value
        .map((v) => {
          const option = selectProps.options.find(
            (opt) => String(opt.value) === String(v),
          );

          if (!option) return null;

          return option;
        })
        .filter(Boolean) as IAutocompleteOption<T>[];
    }
    const option = selectProps.options.find(
      (opt) => String(opt.value) === String(value),
    );

    return option ?? null;
  }, [selectProps.multiple, selectProps.options, value]);

  useEffect(() => {
    // sync внешнего value в внутренний option/option[]
    if (selectProps.multiple) {
      const next = (computedFieldValue || []) as IAutocompleteOption<T>[];
      const cur = (fieldValue || []) as IAutocompleteOption<T>[];
      const a = next.map((o) => o.value);
      const b = cur.map((o) => o.value);
      const same = a.length === b.length && a.every((v, i) => v === b[i]);
      if (!same) setFieldValue(next);
    } else {
      const next = (computedFieldValue ??
        null) as IAutocompleteOption<T> | null;
      const cur = (fieldValue ?? null) as IAutocompleteOption<T> | null;
      const same = (next?.value ?? null) === (cur?.value ?? null);
      if (!same) setFieldValue(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedFieldValue, selectProps.multiple]);

  return (
    <AutocompleteFieldInner<T>
      value={fieldValue}
      defaultValue={(selectProps.multiple ? [] : null) as any}
      noOptionsText={"Ничего не найдено"}
      ref={ref}
      {...selectProps}
      isOptionEqualToValue={(opt, val) => {
        const v = (val as any)?.value ?? val;

        return opt.value === v;
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange={(onChange ? onFieldChange : undefined) as any}
      renderTags={(value) => {
        if (!selectProps.multiple) return null;

        if (value.length === 1) {
          return value[0].name;
        }

        if (value.length > 1) {
          return String(value.length);
        }

        return null;
      }}
    />
  );
}

export default React.forwardRef(AutocompleteField) as <
  T = string,
  Multiple = true,
>(
  props: IAutocompleteFieldProps<T, Multiple> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof AutocompleteField>;
